import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Leaf, Globe, TrendingUp, Shield, Zap, TreeDeciduous } from 'lucide-react';
import Navbar from '../components/Navbar';

export default function ClimatePage() {
  const navigate = useNavigate();

  return (
    <div className="page-wrapper">
      <Navbar />
      <div className="page-content">
        {/* Hero Banner */}
        <section className="climate-hero">
          <div className="climate-hero-overlay"></div>
          <div className="climate-hero-content">
            <div className="climate-badge"><Globe size={14} /> LAUNCHING SOON</div>
            <h1>Climate Solutions <br />by BoAI</h1>
            <h2 className="hindi-text">BoAI द्वारा जलवायु समाधान</h2>
            <p className="climate-hero-desc">
              We are committed to tackling climate change and performing on the global stage.
              BoAI is building AI-powered climate solutions to help farmers earn from sustainability while protecting our planet.
            </p>
            <p className="climate-hero-desc-hi">
              हम जलवायु परिवर्तन से निपटने और वैश्विक मंच पर प्रदर्शन करने के लिए प्रतिबद्ध हैं।
            </p>
          </div>
        </section>

        {/* Solutions Grid */}
        <section className="climate-solutions">
          <div className="container">
            <div className="section-header-centered">
              <h2>About to Launch: Climate Solutions for Agriculture</h2>
              <h3 className="hindi-text">कृषि के लिए जलवायु समाधान जल्द लॉन्च हो रहे हैं</h3>
              <p className="climate-intro">Combining AI with sustainability to create new revenue streams for farmers and a healthier planet</p>
            </div>

            <div className="climate-grid">
              <div className="climate-card">
                <div className="climate-card-image">
                  <img src="https://images.unsplash.com/photo-1761131222698-975921f755de?w=500" alt="Climate Change & Sustainability" />
                </div>
                <div className="climate-card-body">
                  <div className="climate-card-number">01</div>
                  <h3>Climate Change & Sustainability</h3>
                  <h4 className="hindi-text">जलवायु परिवर्तन और स्थिरता</h4>
                  <p>AI-powered climate modeling for agriculture — predicting weather extremes, drought patterns, and helping farmers adapt their practices for a changing world.</p>
                  <ul className="climate-features">
                    <li><Shield size={14} /> Climate risk assessment for farms</li>
                    <li><Shield size={14} /> Adaptive cropping recommendations</li>
                    <li><Shield size={14} /> Water conservation strategies</li>
                  </ul>
                </div>
              </div>

              <div className="climate-card">
                <div className="climate-card-image">
                  <img src="https://images.unsplash.com/photo-1599511491576-ab06653a4ace?w=500" alt="Carbon Credits" />
                </div>
                <div className="climate-card-body">
                  <div className="climate-card-number">02</div>
                  <h3>Carbon Credits & Environmental Markets</h3>
                  <h4 className="hindi-text">कार्बन क्रेडिट और पर्यावरण बाजार</h4>
                  <p>Helping farmers monetize their sustainable practices through verified carbon credits. Turn eco-friendly farming into a new income stream on global environmental markets.</p>
                  <ul className="climate-features">
                    <li><TrendingUp size={14} /> Carbon credit generation & trading</li>
                    <li><TrendingUp size={14} /> MRV (Measurement, Reporting, Verification)</li>
                    <li><TrendingUp size={14} /> Global marketplace access for farmers</li>
                  </ul>
                </div>
              </div>

              <div className="climate-card">
                <div className="climate-card-image">
                  <img src="https://images.pexels.com/photos/35395321/pexels-photo-35395321.jpeg?auto=compress&cs=tinysrgb&w=500" alt="Sustainable Agriculture" />
                </div>
                <div className="climate-card-body">
                  <div className="climate-card-number">03</div>
                  <h3>Sustainable Agriculture & Climate-Smart Solutions</h3>
                  <h4 className="hindi-text">टिकाऊ कृषि और जलवायु-स्मार्ट समाधान</h4>
                  <p>Precision agriculture tools powered by AI to reduce chemical usage, optimize water, and implement regenerative farming at scale — making every farm climate-smart.</p>
                  <ul className="climate-features">
                    <li><Leaf size={14} /> Precision input management (fertilizers, pesticides)</li>
                    <li><Leaf size={14} /> Regenerative farming guidance</li>
                    <li><Leaf size={14} /> Soil carbon sequestration tracking</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* More Coming Soon */}
            <div className="climate-more">
              <div className="climate-more-inner">
                <h3>And Many More Climate Initiatives Coming...</h3>
                <h4 className="hindi-text">और भी कई जलवायु पहलें आ रही हैं...</h4>
                <div className="climate-more-grid">
                  <div className="climate-more-item">
                    <Zap size={24} />
                    <span>Renewable Energy Integration for Farms</span>
                  </div>
                  <div className="climate-more-item">
                    <TreeDeciduous size={24} />
                    <span>Agroforestry & Biodiversity Programs</span>
                  </div>
                  <div className="climate-more-item">
                    <Globe size={24} />
                    <span>Global Climate Data Intelligence</span>
                  </div>
                  <div className="climate-more-item">
                    <Shield size={24} />
                    <span>Climate Insurance & Risk Mitigation</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Commitment Section */}
        <section className="climate-commitment">
          <div className="container">
            <div className="commitment-content">
              <img src="https://images.unsplash.com/photo-1776918570429-244d456317aa?w=600" alt="Solar Farm" className="commitment-image" />
              <div className="commitment-text-block">
                <h2>Our Global Commitment</h2>
                <h3 className="hindi-text">हमारी वैश्विक प्रतिबद्धता</h3>
                <p>
                  BoAI is committed to tackling climate change and performing on the global stage.
                  We believe agriculture is not just the victim of climate change — it can be the solution.
                  By combining AI intelligence with sustainable farming practices, we're building the infrastructure
                  for farmers to earn from carbon markets, adopt regenerative techniques, and contribute to a
                  net-zero future — all while increasing their income.
                </p>
                <p className="hindi-text commitment-hi">
                  BoAI जलवायु परिवर्तन से निपटने और वैश्विक मंच पर प्रदर्शन करने के लिए प्रतिबद्ध है।
                  हम मानते हैं कि कृषि जलवायु परिवर्तन का केवल शिकार नहीं है — यह समाधान हो सकती है।
                  AI और टिकाऊ खेती को मिलाकर, हम किसानों को कार्बन बाजारों से कमाई करने और नेट-जीरो भविष्य में योगदान करने का अवसर दे रहे हैं।
                </p>
                <button className="btn-primary" onClick={() => navigate('/auth')}>
                  Join the Climate Movement
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
